<script setup>
/**
 * 官方公告弹窗：参考常见「防骗公告」结构，视觉用站内黑金。
 * 改 NOTICE_VERSION 可让老用户再次看到新公告。
 */
import { computed, onMounted, ref } from "vue";
import { useLangStore } from "@/stores/lang.js";
import { useModalA11y } from "@/composables/useModalA11y.js";
import {
  OFFICIAL_TG_HANDLE,
  OFFICIAL_TG_URL,
  OFFICIAL_X_HANDLE,
  OFFICIAL_X_URL,
} from "@/lib/officialChannels.js";
import "@/assets/official-notice.css";

const NOTICE_VERSION = "fx-notice-v2";
const STORAGE_KEY = "flap_official_notice_ack";

const lang = useLangStore();
const open = ref(false);
const dialogRef = useModalA11y(
  computed(() => open.value),
  dismiss
);

const blocks = computed(() => [
  {
    leadZh: "重要提醒：",
    leadEn: "Important:",
    bodyZh:
      "旧推特 @fxinflap 已封，请勿再信。官方 X 为 @Mariianiitha21，闲聊群为 Telegram @Flapfxin。任何人私聊索要私钥、助记词、或让你「转账验证」，一律视为诈骗。",
    bodyEn:
      "The old X @fxinflap is banned — ignore it. Official X is @Mariianiitha21; chat group is Telegram @Flapfxin. Anyone DMing for keys/seed phrases, or asking you to “transfer to verify”, is a scam.",
  },
  {
    leadZh: "各位注意：",
    leadEn: "Please note:",
    bodyZh:
      "金库工厂地址、填参说明以本站页面列表为准。不要使用来路不明的「同款工厂」或第三方代挂链接。发射前请自行核对合约与参数。",
    bodyEn:
      "Factory addresses and fill notes on this site are authoritative. Do not use unverified “same factory” links or third-party launch helpers. Always double-check contracts before launch.",
  },
]);

function dismiss() {
  open.value = false;
  try {
    localStorage.setItem(STORAGE_KEY, NOTICE_VERSION);
  } catch {
    /* ignore */
  }
}

onMounted(() => {
  try {
    if (localStorage.getItem(STORAGE_KEY) === NOTICE_VERSION) return;
  } catch {
    /* show anyway */
  }
  // 稍晚弹出，避免抢首屏
  window.setTimeout(() => {
    open.value = true;
  }, 600);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fx-notice"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="'fx-notice-title'"
      @click.self="dismiss"
    >
      <div ref="dialogRef" class="fx-notice-panel" tabindex="-1">
        <header class="fx-notice-head">
          <div class="fx-notice-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 3l7 3v5c0 4.5-2.9 7.8-7 9-4.1-1.2-7-4.5-7-9V6l7-3z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M12 8v5M12 16.5h.01"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="fx-notice-titles">
            <div class="fx-notice-title-row">
              <h2 id="fx-notice-title">
                {{ lang.t("封心 · 官方公告", "Fengxin · Official notice") }}
              </h2>
              <span class="fx-notice-tag">{{
                lang.t("重要 · 谨防受骗", "Important · Beware of scams")
              }}</span>
            </div>
            <p class="fx-notice-sub">
              {{
                lang.t(
                  "请仔细阅读，认准官方渠道，谨防冒充欺诈。",
                  "Read carefully. Use official channels only. Beware of impersonation."
                )
              }}
            </p>
          </div>
          <button
            type="button"
            class="fx-notice-close"
            :aria-label="lang.t('关闭', 'Close')"
            @click="dismiss"
          >
            ×
          </button>
        </header>

        <div class="fx-notice-body">
          <div v-for="(b, i) in blocks" :key="i" class="fx-notice-card">
            <p>
              <strong>{{ lang.t(b.leadZh, b.leadEn) }}</strong>
              {{ lang.t(b.bodyZh, b.bodyEn) }}
            </p>
          </div>
          <p class="fx-notice-channels">
            <a :href="OFFICIAL_X_URL" target="_blank" rel="noopener noreferrer">X {{ OFFICIAL_X_HANDLE }}</a>
            <span aria-hidden="true">·</span>
            <a :href="OFFICIAL_TG_URL" target="_blank" rel="noopener noreferrer">Telegram {{ OFFICIAL_TG_HANDLE }}</a>
          </p>
        </div>

        <footer class="fx-notice-foot">
          <button type="button" class="fx-notice-ack" @click="dismiss">
            <span class="fx-notice-ack-ico" aria-hidden="true">✓</span>
            {{ lang.t("我知道了，谨记在心", "Got it — I’ll keep this in mind") }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
